import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getQRTranslation, getAdvertisements } from './constants';
import { FlashIcon } from './FlashIcon';
import { QRFrame } from './QRFrame';
import { CameraView } from './CameraView';
import { AdvertisementBanner } from './AdvertisementBanner';
import { useQRCamera } from './useQRCamera';
import { useLanguage } from '@/contexts/AppContext';

export function QRScanner() {
  const { locale } = useLanguage();
  const navigate = useNavigate();

  const handleScanResult = useCallback((qrCode: string) => {
    navigate(`/qr/${qrCode}`);
  }, [navigate]);

  const { flashOn, cameraStatus, cameraError, videoRef, handleFlashToggle, startCamera } = useQRCamera({
    onScanResult: handleScanResult,
  });

  const t = getQRTranslation(locale);
  const advertisements = getAdvertisements(locale);

  return (
    <div className="flex min-h-[70vh] w-full flex-col items-center gap-6 pb-6">
      {/* Advertisement Banner */}
      <section
        aria-label="QR promotions"
        data-testid="qr-advertisement-region"
        className="w-screen"
      >
        <AdvertisementBanner advertisements={advertisements} />
      </section>

      <div
        data-testid="qr-scanner-controls"
        className="flex w-full max-w-md flex-col items-center gap-6 px-5"
      >
        {/* Flash Toggle */}
        <div className="w-full flex justify-center py-2">
          <div className="flex flex-col items-center gap-1.5">
            <FlashIcon
              isOn={flashOn}
              onClick={handleFlashToggle}
              ariaLabelOn={t.flashOnAriaLabel}
              ariaLabelOff={t.flashOffAriaLabel}
            />
            <p className="text-[16px] text-foreground">
              {t.flashText}
            </p>
          </div>
        </div>

        {/* QR Scanner Frame with Camera */}
        <div className="w-full flex justify-center py-2">
          <div className="w-[240px]">
            <QRFrame>
              <CameraView
                cameraStatus={cameraStatus}
                cameraError={cameraError}
                videoRef={videoRef}
                onCameraRetry={startCamera}
                translation={t}
              />
            </QRFrame>
          </div>
        </div>

        {/* Scan Text */}
        <div className="w-full text-center">
          <p className="text-[18px] text-foreground">
            {t.scanText}
          </p>
        </div>
      </div>
    </div>
  );
}
